/**
 * ACTIVITY-CORE — human text for household feed (i18n).
 *
 * App-SPECIFIC part: the entity_type+action → warm phrase map. The
 * structure (patch, grouping, offline) lives in @mealplan/shared. Returns
 * `msg` descriptors (not strings) so language is reactive.
 *
 * NAMES: patch only includes reliable name in created/deleted snapshots; a
 * `checked` toggle is just `{checked}`. That is why `activitySentence` accepts a
 * RESOLVER that resolves the name from LOCAL tables by (type, id):
 * shopping_item.name, recipe.name, pantry_item.name; and for meal_plan_entry the
 * recipe name via entry.recipe_id → recipe.name. Falls back to name from
 * patch and, if none, to a warm anonymous phrase. Never throws.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@mealplan/shared";

/** Resolves the visible name of an entity from local SQLite. */
export type EntityNameResolver = (
  entityType: string | null,
  entityId: string | null,
) => string | null;

interface ActivitySentence {
  /** Phrase with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Unnamed phrase (patch does not include it and could not be resolved). */
  anonymous: MessageDescriptor;
}

/**
 * Fine semantic key: refines `updated`/`deleted` with what actually happened
 * (marking bought, moving to trash…) by inspecting the patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "shopping_item" && action === "updated" && patch.checked) {
    return patch.checked.new ? "checked" : "unchecked";
  }
  if (action === "deleted") {
    // Only recipe has trash: transition of deleted_at = to trash;
    // full snapshot (without deleted_at) = definitive purge. Other
    // entities (shopping/planner/pantry) are hard-deleted → "deleted".
    if (row.entity_type === "recipe") {
      return patch.deleted_at ? "trashed" : "purged";
    }
    return "deleted";
  }
  return action;
}

const SHOPPING_ITEM_TEXTS: Record<string, ActivitySentence> = {
  checked: {
    named: msg`marked {name} as bought`,
    anonymous: msg`marked an item as bought`,
  },
  unchecked: { named: msg`unmarked {name}`, anonymous: msg`unmarked an item` },
  created: { named: msg`added {name} to the list`, anonymous: msg`added an item to the list` },
  deleted: {
    named: msg`removed {name} from the list`,
    anonymous: msg`removed an item from the list`,
  },
  updated: { named: msg`edited the item {name}`, anonymous: msg`edited an item` },
};

const MEAL_PLAN_ENTRY_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`planned {name}`, anonymous: msg`planned a meal` },
  deleted: {
    named: msg`removed {name} from the planner`,
    anonymous: msg`removed a meal from the planner`,
  },
  updated: {
    named: msg`updated {name} in the planner`,
    anonymous: msg`changed a meal in the planner`,
  },
};

const RECIPE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the recipe {name}`, anonymous: msg`added a recipe` },
  updated: { named: msg`edited the recipe {name}`, anonymous: msg`edited a recipe` },
  trashed: { named: msg`moved {name} to the trash`, anonymous: msg`moved a recipe to the trash` },
  purged: {
    named: msg`permanently deleted {name}`,
    anonymous: msg`permanently deleted a recipe`,
  },
  restored: { named: msg`restored {name}`, anonymous: msg`restored a recipe` },
  reverted: { named: msg`undid a change on {name}`, anonymous: msg`undid a change` },
};

const PANTRY_ITEM_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added {name} to the pantry`, anonymous: msg`added an item to the pantry` },
  deleted: {
    named: msg`removed {name} from the pantry`,
    anonymous: msg`removed an item from the pantry`,
  },
  updated: { named: msg`updated {name} in the pantry`, anonymous: msg`updated a pantry item` },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed {name}`,
  anonymous: msg`made a change`,
};

const TABLES: Record<string, Record<string, ActivitySentence>> = {
  shopping_item: SHOPPING_ITEM_TEXTS,
  meal_plan_entry: MEAL_PLAN_ENTRY_TEXTS,
  recipe: RECIPE_TEXTS,
  pantry_item: PANTRY_ITEM_TEXTS,
};

/** Visible name extracted ONLY from the patch (name/text, new or old). */
function nameFromPatch(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["name", "text"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * Visible name of the entity: first the resolver (local tables), then the
 * patch. `null` if unable to resolve (anonymous phrase will be used).
 */
export function activityEntityName(row: ActivityRow, resolve?: EntityNameResolver): string | null {
  const resolved = resolve?.(row.entity_type, row.entity_id);
  if (resolved && resolved.trim().length > 0) {
    return resolved;
  }
  return nameFromPatch(row);
}

/**
 * i18n phrase for an entry: descriptor + values. Render with
 * `t(descriptor, values)` in the component.
 */
export function activitySentence(
  row: ActivityRow,
  resolve?: EntityNameResolver,
): { descriptor: MessageDescriptor; values: Record<string, string> } {
  const key = activitySemanticKey(row);
  const table = row.entity_type ? (TABLES[row.entity_type] ?? {}) : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row, resolve);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "5 min ago" localized with Intl; falls back to day/date for older items. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
